import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const AgenticAICourse = () => {
  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: "Enroll in 10x Agentic AI Program",
    subtitle: "Start your journey from AI Outsider to AI Founder in 4 months",
    formType: "course_enrollment",
    interestedProgram: "10x Agentic AI and Automation Mastery"
  });

  const openPopup = (config: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  } = {}) => {
    setPopupState({
      isOpen: true,
      title: config.title || "Enroll in 10x Agentic AI Program",
      subtitle: config.subtitle || "Start your journey from AI Outsider to AI Founder in 4 months",
      formType: config.formType || "course_enrollment",
      interestedProgram: config.interestedProgram || "10x Agentic AI and Automation Mastery"
    });
  };

  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  };

  // Cursor tracker effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const tracker = document.getElementById('cursor-tracker');
      const section = document.querySelector('.cursor-tracker-section');
      
      if (tracker && section) {
        const rect = section.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Only track if cursor is within the section
        if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
          tracker.style.left = `${x - 8}px`; // Offset by half the tracker size
          tracker.style.top = `${y - 8}px`;
          tracker.style.opacity = '0.8';
        } else {
          tracker.style.opacity = '0';
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header openPopup={openPopup} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white min-h-screen flex items-center justify-center overflow-hidden">
        {/* Agentic AI & Automation Background Elements */}
        <div className="absolute inset-0">
          {/* Dark background */}
          <div className="absolute inset-0 bg-gray-900"></div>
          
          {/* AI Agent & Workflow visualization elements */}
          <div className="absolute inset-0 overflow-hidden opacity-25">
            {/* Workflow Trigger Nodes */}
            <div className="absolute top-20 left-16">
              <div className="space-y-4">
                {/* Trigger Node */}
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 bg-green-400 rounded-full animate-pulse flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div className="text-xs text-green-300 font-mono">TRIGGER</div>
                </div>
                {/* Processing Nodes */}
                <div className="flex items-center space-x-3 ml-2">
                  <div className="w-3 h-3 bg-blue-400 rounded animate-pulse delay-100"></div>
                  <div className="text-xs text-blue-300 font-mono">AI PROCESS</div>
                </div>
                <div className="flex items-center space-x-3 ml-4">
                  <div className="w-3 h-3 bg-purple-400 rounded animate-pulse delay-200"></div>
                  <div className="text-xs text-purple-300 font-mono">DECISION</div>
                </div>
                <div className="flex items-center space-x-3 ml-6">
                  <div className="w-3 h-3 bg-yellow-400 rounded animate-pulse delay-300"></div>
                  <div className="text-xs text-yellow-300 font-mono">ACTION</div>
                </div>
              </div>
            </div>

            {/* Agentic Workflow Diagram */}
            <div className="absolute top-32 right-20">
              <svg width="120" height="80" className="stroke-purple-400">
                {/* Workflow connections */}
                <path
                  fill="none"
                  stroke="#8B5CF6"
                  strokeWidth="2"
                  strokeDasharray="4,4"
                  d="M10,40 L30,40 L30,20 L50,20 L50,40 L70,40 L70,60 L90,60"
                  className="animate-pulse"
                />
                {/* Agent nodes */}
                <circle cx="10" cy="40" r="4" fill="#10B981" className="animate-pulse" />
                <circle cx="50" cy="20" r="4" fill="#3B82F6" className="animate-pulse delay-100" />
                <circle cx="70" cy="40" r="4" fill="#8B5CF6" className="animate-pulse delay-200" />
                <circle cx="90" cy="60" r="4" fill="#F59E0B" className="animate-pulse delay-300" />
                {/* Mini labels */}
                <text x="12" y="35" fill="#10B981" fontSize="6" className="font-mono">INPUT</text>
                <text x="52" y="15" fill="#3B82F6" fontSize="6" className="font-mono">AI</text>
                <text x="72" y="35" fill="#8B5CF6" fontSize="6" className="font-mono">LOGIC</text>
                <text x="92" y="55" fill="#F59E0B" fontSize="6" className="font-mono">OUT</text>
              </svg>
            </div>

            {/* AI Agent Brain with Reasoning */}
            <div className="absolute bottom-32 left-24">
              <div className="relative">
                <svg width="90" height="90" className="animate-spin" style={{ animationDuration: '20s' }}>
                  <circle cx="45" cy="45" r="40" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="15 5" />
                  <circle cx="45" cy="45" r="28" fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="10 5" />
                  <circle cx="45" cy="45" r="16" fill="none" stroke="#10B981" strokeWidth="2" strokeDasharray="8 3" />
                  <circle cx="45" cy="45" r="6" fill="#EF4444" />
                </svg>
                {/* Agent reasoning indicators */}
                <div className="absolute top-2 left-2 text-xs text-purple-300 font-mono animate-pulse">AGENT</div>
                <div className="absolute bottom-2 right-2 text-xs text-blue-300 font-mono animate-pulse delay-500">THINK</div>
              </div>
            </div>

            {/* n8n-style Workflow Blocks */}
            <div className="absolute top-1/2 left-12">
              <div className="space-y-3">
                {/* Row 1 - Data Input Flow */}
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-6 bg-green-400 rounded flex items-center justify-center animate-pulse">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <div className="w-1 h-0.5 bg-gray-400"></div>
                  <div className="w-10 h-6 bg-blue-400 rounded flex items-center justify-center animate-pulse delay-100">
                    <div className="text-xs text-white font-bold">AI</div>
                  </div>
                  <div className="w-1 h-0.5 bg-gray-400"></div>
                  <div className="w-6 h-6 bg-purple-400 rounded flex items-center justify-center animate-pulse delay-200">
                    <div className="w-2 h-2 bg-white rounded"></div>
                  </div>
                </div>
                {/* Row 2 - Decision Flow */}
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-6 bg-yellow-400 rounded flex items-center justify-center animate-pulse delay-150">
                    <div className="text-xs text-black font-bold">IF</div>
                  </div>
                  <div className="w-1 h-0.5 bg-gray-400"></div>
                  <div className="w-8 h-6 bg-red-400 rounded flex items-center justify-center animate-pulse delay-250">
                    <div className="w-3 h-3 bg-white rounded"></div>
                  </div>
                  <div className="w-1 h-0.5 bg-gray-400"></div>
                  <div className="w-12 h-6 bg-pink-400 rounded flex items-center justify-center animate-pulse delay-350">
                    <div className="text-xs text-white font-bold">DO</div>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Agent Network - Multiple Agents */}
            <div className="absolute bottom-20 right-16">
              <div className="relative">
                {/* Main Agent */}
                <div className="w-10 h-10 bg-purple-500 rounded-lg animate-bounce flex items-center justify-center">
                  <div className="text-white text-xs font-bold">🤖</div>
                </div>
                {/* Sub Agents */}
                <div className="absolute top-2 left-12 w-8 h-8 bg-blue-500 rounded-lg animate-bounce delay-100 flex items-center justify-center">
                  <div className="text-white text-xs">🔍</div>
                </div>
                <div className="absolute -top-2 left-6 w-6 h-6 bg-green-500 rounded-lg animate-bounce delay-200 flex items-center justify-center">
                  <div className="text-white text-xs">✓</div>
                </div>
                <div className="absolute top-10 left-3 w-7 h-7 bg-yellow-500 rounded-lg animate-bounce delay-300 flex items-center justify-center">
                  <div className="text-white text-xs">📧</div>
                </div>
                {/* Connection lines */}
                <svg className="absolute inset-0 w-20 h-16 pointer-events-none">
                  <line x1="10" y1="10" x2="16" y2="8" stroke="#8B5CF6" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse" />
                  <line x1="10" y1="10" x2="14" y2="4" stroke="#8B5CF6" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse delay-100" />
                  <line x1="10" y1="10" x2="8" y2="16" stroke="#8B5CF6" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse delay-200" />
                </svg>
              </div>
            </div>

            {/* Automation Flow Gears */}
            <div className="absolute top-3/4 right-1/3">
              <div className="relative">
                <svg width="60" height="60" className="text-purple-400 animate-spin" fill="currentColor" viewBox="0 0 24 24" style={{ animationDuration: '12s' }}>
                  <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5M19.43 12.98C19.47 12.66 19.5 12.34 19.5 12C19.5 11.66 19.47 11.34 19.43 11.02L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.97 19.05 5.05L16.56 6.05C16.04 5.65 15.48 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.52 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.21 8.95 2.27 9.22 2.46 9.37L4.57 11.02C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.66 4.57 12.98L2.46 14.63C2.27 14.78 2.21 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.95C7.96 18.35 8.52 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.48 18.68 16.04 18.34 16.56 17.95L19.05 18.95C19.27 19.04 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.98Z"/>
                </svg>
                <div className="absolute top-14 left-2 text-xs text-purple-300 font-mono animate-pulse">AUTO</div>
              </div>
            </div>

            {/* Data Flow Indicators */}
            <div className="absolute top-16 right-1/3">
              <div className="space-y-2">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-ping"></div>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-ping delay-100"></div>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-ping delay-200"></div>
                  <div className="text-xs text-green-300 font-mono">DATA FLOW</div>
                </div>
              </div>
            </div>

            {/* Vector Database Indicator */}
            <div className="absolute bottom-16 left-1/3">
              <div className="relative">
                <div className="w-12 h-8 bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded animate-pulse">
                  <div className="absolute inset-1 space-y-0.5">
                    <div className="h-0.5 bg-white/70 rounded"></div>
                    <div className="h-0.5 bg-white/70 rounded w-3/4"></div>
                    <div className="h-0.5 bg-white/70 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="absolute -bottom-4 left-0 text-xs text-blue-300 font-mono">VECTOR DB</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 pt-2 pb-8">
          <div className="text-center">
            {/* AI Builder Badge */}
            <div className="inline-flex items-center bg-gradient-to-r from-purple-500/30 to-purple-600/40 backdrop-blur-lg text-white px-8 py-4 rounded-2xl text-base font-bold mb-6 border-2 border-purple-400/50 shadow-lg shadow-purple-500/25">
              <div className="flex items-center bg-white rounded-lg px-3 py-1 mr-3">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">🤖</span>
                  <span className="text-gray-700 font-semibold text-sm">AI BUILDER</span>
                </div>
              </div>
              <span className="text-purple-100">1to10x PROGRAM</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight tracking-tight">
              <span className="inline-block animate-pulse">10X</span>{" "}
              <span className="text-white">Agentic AI and</span>{" "}
              <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-green-400 bg-clip-text text-transparent font-extrabold">
                Automation Mastery
              </span>
            </h1>
            
            {/* Subtitle */}
            <div className="mb-8 max-w-4xl mx-auto">
              <p className="text-lg sm:text-xl text-gray-300 font-light leading-relaxed">
                <span className="text-yellow-400 font-semibold">From AI Outsider to AI Founder</span> in 4 months.{" "}
                <span className="text-purple-400 font-semibold">Build, Deploy & Sell AI Automation Systems</span> that work while you sleep.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 max-w-4xl mx-auto">
              <div className="group relative overflow-hidden bg-gradient-to-br from-purple-500/20 to-purple-600/30 backdrop-blur-lg rounded-2xl p-5 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300 hover:scale-105">
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 mx-auto text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-purple-200 mb-1 uppercase tracking-wider">4 Months</div>
                  <div className="text-white font-bold text-sm">AI Transformation</div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden bg-gradient-to-br from-blue-500/20 to-blue-600/30 backdrop-blur-lg rounded-2xl p-5 border border-blue-400/30 hover:border-blue-400/60 transition-all duration-300 hover:scale-105">
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 mx-auto text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-blue-200 mb-1 uppercase tracking-wider">8 Sprints</div>
                  <div className="text-white font-bold text-sm">Intensive Training</div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden bg-gradient-to-br from-green-500/20 to-green-600/30 backdrop-blur-lg rounded-2xl p-5 border border-green-400/30 hover:border-green-400/60 transition-all duration-300 hover:scale-105">
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 mx-auto text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-green-200 mb-1 uppercase tracking-wider">100+ Projects</div>
                  <div className="text-white font-bold text-sm">AI Automation</div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden bg-gradient-to-br from-yellow-500/20 to-yellow-600/30 backdrop-blur-lg rounded-2xl p-5 border border-yellow-400/30 hover:border-yellow-400/60 transition-all duration-300 hover:scale-105">
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 mx-auto text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-yellow-200 mb-1 uppercase tracking-wider">AI Founder</div>
                  <div className="text-white font-bold text-sm">Status Unlocked</div>
                </div>
              </div>
            </div>

            {/* Call-to-Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full px-4 mt-6">
              <a 
                href="https://academy.10xdataanalyst.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm text-center whitespace-nowrap inline-block"
                style={{ minWidth: 'max-content' }}
              >
                Enrol Now
              </a>
              <button 
                onClick={() => openPopup({
                  title: "Book FREE Strategy Call",
                  subtitle: "Connect with our AI automation experts to discuss your transformation goals",
                  formType: "strategy_call"
                })}
                className="border border-white/50 text-white hover:bg-white hover:text-gray-900 px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm text-center whitespace-nowrap inline-block"
                style={{ minWidth: 'max-content' }}
              >
                Book FREE Strategy Call
              </button>
            </div>

            {/* Rating */}
            <div className="mt-8 text-center">
              <p className="text-gray-300">
                This program is rated as <span className="text-yellow-400 font-bold">excellent</span> by 95% of AI builders
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Benefits */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The 5-Stage <span className="text-purple-600">AI Transformation</span> System
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From AI Outsider to AI Founder - your complete identity transformation in 4 months
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
            {[
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014.846 21H9.154a3.374 3.374 0 00-1.849-1.713L6.757 19z" />
                  </svg>
                ),
                title: "AI Outsider → AI User",
                description: "Build your first AI-powered automations with n8n and OpenAI. Learn trigger thinking and master the flow logic that powers intelligent systems."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
                title: "AI User → AI Operator",
                description: "Master workflow logic, vector intelligence, and RAG systems. Build AI agents with memory that understand context and make intelligent decisions."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                ),
                title: "AI Operator → AI Architect",
                description: "Ship 30 AI projects in 30 days across HR, Sales, Marketing, and Operations. Master the demo engine and presentation stack that wins clients."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0l.879-.659M6.13 15.055a48.694 48.694 0 015.757-.442c1.934 0 3.843.15 5.757.442m-11.514 0a48.717 48.717 0 00-2.447.465c-.943.244-1.763 1.07-1.763 2.15v1.23c0 .98.626 1.813 1.512 2.132A47.775 47.775 0 006.13 21.75M17.87 15.055c.867-.05 1.732-.105 2.598-.165.943-.066 1.763-.892 1.763-1.972v-1.23c0-.98-.626-1.813-1.512-2.132A47.775 47.775 0 0017.87 9.25" />
                  </svg>
                ),
                title: "AI Architect → AI Founder",
                description: "Learn to productize AI workflows, build client-ready offers, and sell AI automation services. Launch your own AI consulting business."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: "10x AI Builder Status",
                description: "Think automation-first, build agentic systems at scale, and lead the AI transformation in your industry. You're no longer learning AI - you're building the future with it."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                ),
                title: "Global AI Builder Community",
                description: "Join 15+ countries of AI innovators, access lifetime mentorship, and become part of the next generation of automation entrepreneurs."
              }
            ].map((benefit, idx) => (
              <div key={idx} className="text-left">
                {/* Icon */}
                <div className="mb-6">
                  {benefit.icon}
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Curriculum - 8 Sprints */}
      <section className="relative">
        <div className="w-full h-px bg-gray-300 absolute top-0 left-0"></div>
        
        <div className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                The <span className="text-purple-600">8-Sprint</span> AI Mastery Roadmap
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                4 months. 8 powerful sprints. Each sprint transforms your identity and unlocks new AI capabilities.
              </p>
            </div>

            {/* Curriculum Accordion */}
            <div className="max-w-5xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {[
                  {
                    month: "MONTH 1: FOUNDATION",
                    title: "Sprint 1-2: From AI Outsider → AI User",
                    sprints: [
                      {
                        name: "Sprint 1: Trigger Thinking",
                        lessons: [
                          "LLMs & Tokens - What's happening behind prompts",
                          "Prompt Engineering Basics - System prompts, Variables, Roles",
                          "n8n Fundamentals - Triggers, Nodes, and Flow logic",
                          "Integration Layer Thinking - Connect apps like Telegram, Gmail, Notion",
                          "Error Handling & Workflow Debugging",
                          "Build Telegram Bot with AI responses",
                          "Understanding event-data structures",
                          "Trigger types: Manual, Time-based, Web-hook, App Events"
                        ]
                      },
                      {
                        name: "Sprint 2: Mastering The Flow",
                        lessons: [
                          "Data transformation techniques",
                          "Human-In-Loop decision systems", 
                          "Designing fail-safe, scalable workflows",
                          "Sub-flows, reusable blocks, and logic optimization",
                          "Conditional logic: IF, SWITCH, AND/OR branches",
                          "Build AI-generated weekly report system",
                          "Workflow screen-recordings + demo storytelling",
                          "System architecture thinking"
                        ]
                      }
                    ]
                  },
                  {
                    month: "MONTH 2: BUILDER",
                    title: "Sprint 3-4: From AI User → AI Operator", 
                    sprints: [
                      {
                        name: "Sprint 3: Workflow Logic",
                        lessons: [
                          "Advanced logic nodes: IF, SWITCH, SET, MERGE",
                          "Building Multi-condition Flows",
                          "Human-In-Loop approvals",
                          "Dynamic Data Routing",
                          "Full-Stack Workflows",
                          "Lead enrichment bot with verification",
                          "Error handling, retries, fallbacks",
                          "Creating internal control dashboards"
                        ]
                      },
                      {
                        name: "Sprint 4: Vector Intelligence",
                        lessons: [
                          "Embeddings & Document Chunks",
                          "Vector Databases (Supabase, Pinecone)",
                          "RAG (Retrieval-Augmented Generation)",
                          "Custom AI Toolkits for domain-specific tasks",
                          "Multi-modal inputs: Voice, text, buttons",
                          "Build private knowledge bot with company docs",
                          "Long-term memory using temporary sheets",
                          "Agentic Prompting & reasoning chains"
                        ]
                      }
                    ]
                  },
                  {
                    month: "MONTH 3: DEMO",
                    title: "Sprint 5-6: From AI Operator → AI Architect",
                    sprints: [
                      {
                        name: "Sprint 5: The Demo Engine",
                        lessons: [
                          "Fast agentic prototyping",
                          "30 Projects in 30 Days Challenge",
                          "HR: Resume Shortlister + Candidate Evaluator", 
                          "Sales: Lead Qualifier + Follow-Up Bot",
                          "Marketing: Content Generator + Campaign Optimizer",
                          "Finance: Transaction Summarizer + Fraud Monitor",
                          "Operations: Task Tracker + Report Generator",
                          "Deployment to public endpoints"
                        ]
                      },
                      {
                        name: "Sprint 6: The Presentation Stack",
                        lessons: [
                          "Workflow documentation best practices",
                          "Explaining systems to non-technical clients",
                          "Presentation techniques for live demos",
                          "Visual documentation (Looms, Notion, One-Pagers)",
                          "Crafting landing pages for AI builds",
                          "Create portfolio with demo videos",
                          "Client communication frameworks",
                          "Building confidence in presentations"
                        ]
                      }
                    ]
                  },
                  {
                    month: "MONTH 4: HUSTLE",
                    title: "Sprint 7-8: From AI Architect → AI Founder",
                    sprints: [
                      {
                        name: "Sprint 7: Productisation",
                        lessons: [
                          "Packaging workflows like products",
                          "Pricing Models & Positioning strategies",
                          "Offer Design Frameworks (use case > outcome > value)",
                          "Landing page copy for AI tools",
                          "Client onboarding system design",
                          "Packaging systems for industry verticals",
                          "Create One-Pager for non-technical founders",
                          "Internal process automation for fulfillment"
                        ]
                      },
                      {
                        name: "Sprint 8: AI Selling Systems",
                        lessons: [
                          "Building cold outreach agents",
                          "Creating AI CRMs and response systems",
                          "Lead scoring and routing workflows",
                          "Pitch automation via LinkedIn, Telegram, email",
                          "Running client demo loops with bots",
                          "Launch Outreach Campaign using AI Agent",
                          "Book 3 live demo calls challenge",
                          "Building your AI consulting business"
                        ]
                      }
                    ]
                  }
                ].map((module, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`module-${idx}`}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300"
                  >
                    <AccordionTrigger className="px-8 py-6 text-left hover:no-underline hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-4 w-full">
                        <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg">{idx + 1}</span>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-purple-600 font-semibold mb-1">{module.month}</div>
                          <h3 className="text-xl font-bold text-gray-900 text-left">
                            {module.title}
                          </h3>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-8 pb-8 pt-4">
                      <div className="space-y-6">
                        {module.sprints.map((sprint, sprintIdx) => (
                          <div key={sprintIdx} className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6">
                            <h4 className="text-lg font-bold text-purple-700 mb-4">{sprint.name}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {sprint.lessons.map((lesson, lessonIdx) => (
                                <div 
                                  key={lessonIdx} 
                                  className="flex items-start space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] border border-gray-100"
                                >
                                  <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg flex items-center justify-center font-bold text-xs">
                                    {lessonIdx + 1}
                                  </div>
                                  <span className="text-gray-800 leading-relaxed font-medium text-sm">
                                    {lesson}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* AI Projects Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#111827' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="mb-8">
              <span className="text-purple-400 text-lg font-medium">Build automation for any industry in 4 months</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              <span className="text-purple-400">100+</span> Real-World <span className="text-purple-400">AI Projects</span> You'll Build
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              <span className="text-purple-400 font-semibold">AI Employees that work while you sleep.</span> Build automation systems 
              <span className="text-gray-100 font-medium"> which works for YOU.</span>
            </p>
          </div>

          {/* Scrolling Projects Carousel */}
          <div className="relative overflow-hidden mb-12">
            <div className="flex animate-scroll-left space-x-8" style={{
              width: 'calc(400px * 15)', // 15 projects * 400px width
              animation: 'scroll-left 45s linear infinite'
            }}>
              {[
                {
                  domain: "HR",
                  title: "AI Recruitment Assistant",
                  description: "Screens candidates, updates tracker, sends feedback, and evaluates resumes using AI prompts.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6h3v7c0 .55.45 1 1 1h9c.55 0 1-.45 1-1v-7h3v6c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2zM18 7h-2.5c-.71 0-1.36.37-1.73.99L12 10.5 10.23 7.99c-.37-.62-1.02-.99-1.73-.99H6c-1.1 0-2 .9-2 2v3h18V9c0-1.1-.9-2-2-2z"/>
                    </svg>
                  ),
                  features: ["Resume scoring", "Candidate evaluation", "Automated feedback"]
                },
                {
                  domain: "Sales", 
                  title: "Lead Qualification Agent",
                  description: "Qualifies leads using vector search, sends follow-ups, and books demos automatically.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"/>
                    </svg>
                  ),
                  features: ["Lead scoring", "Auto follow-up", "Demo booking"]
                },
                {
                  domain: "Marketing",
                  title: "Content Generation Bot",
                  description: "Creates multi-channel content, optimizes campaigns, and analyzes sentiment.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z"/>
                    </svg>
                  ),
                  features: ["Content creation", "Campaign optimization", "Sentiment analysis"]
                },
                {
                  domain: "Finance",
                  title: "Document Processor",
                  description: "Summarizes financial documents, classifies invoices, and parses expenses from Gmail.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                    </svg>
                  ),
                  features: ["Document analysis", "Invoice routing", "Expense tracking"]
                },
                {
                  domain: "Operations",
                  title: "Task Management System",
                  description: "Routes tickets, analyzes workspace data, and summarizes meeting notes automatically.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  ),
                  features: ["Ticket routing", "Workspace analysis", "Meeting summaries"]
                },
                {
                  domain: "Education",
                  title: "AI Learning Assistant",
                  description: "Provides revision support, creates voice quizzes, and generates content from PDFs.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
                    </svg>
                  ),
                  features: ["AI tutoring", "Quiz generation", "Voice interaction"]
                },
                {
                  domain: "Healthcare",
                  title: "Medical Records Bot",
                  description: "Processes patient data, schedules appointments, and sends health reminders automatically.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 8h-2v3h-3v2h3v3h2v-3h3v-2h-3V8zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z"/>
                    </svg>
                  ),
                  features: ["Patient data processing", "Appointment scheduling", "Health reminders"]
                },
                {
                  domain: "E-commerce",
                  title: "Inventory Manager",
                  description: "Tracks stock levels, predicts demand, and automates reordering workflows.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7 4V2C7 1.45 7.45 1 8 1h8c.55 0 1 .45 1 1v2h4c.55 0 1 .45 1 1s-.45 1-1 1h-1v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V6H3c-.55 0-1-.45-1-1s.45-1 1-1h4zM9 3v1h6V3H9zm2 12c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55.45 1 1 1zm2-6v5c0 .55.45 1 1 1s1-.45 1-1V9c0-.55-.45-1-1-1s-1 .45-1 1z"/>
                    </svg>
                  ),
                  features: ["Stock tracking", "Demand prediction", "Auto reordering"]
                },
                {
                  domain: "Legal",
                  title: "Contract Analyzer",
                  description: "Reviews legal documents, extracts key terms, and flags potential issues.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/>
                    </svg>
                  ),
                  features: ["Document review", "Term extraction", "Risk assessment"]
                },
                {
                  domain: "Real Estate",
                  title: "Property Evaluator",
                  description: "Analyzes market trends, estimates property values, and generates reports.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                    </svg>
                  ),
                  features: ["Market analysis", "Property valuation", "Report generation"]
                },
                {
                  domain: "Customer Service",
                  title: "Support Ticket Router",
                  description: "Categorizes tickets, assigns priority levels, and routes to appropriate teams.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"/>
                    </svg>
                  ),
                  features: ["Ticket categorization", "Priority assignment", "Team routing"]
                },
                {
                  domain: "Manufacturing",
                  title: "Quality Control Agent",
                  description: "Monitors production data, detects anomalies, and triggers quality alerts.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ),
                  features: ["Production monitoring", "Anomaly detection", "Quality alerts"]
                },
                {
                  domain: "Travel",
                  title: "Trip Planning Assistant",
                  description: "Books flights, finds accommodations, and creates personalized itineraries.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                    </svg>
                  ),
                  features: ["Flight booking", "Hotel search", "Itinerary creation"]
                },
                {
                  domain: "Food & Beverage",
                  title: "Menu Optimizer",
                  description: "Analyzes sales data, suggests menu changes, and manages supplier orders.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                    </svg>
                  ),
                  features: ["Sales analysis", "Menu optimization", "Supplier management"]
                },
                {
                  domain: "Logistics",
                  title: "Route Optimization Bot",
                  description: "Plans delivery routes, tracks shipments, and optimizes delivery schedules.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                  ),
                  features: ["Route planning", "Shipment tracking", "Schedule optimization"]
                }
              ].concat([
                // Duplicate the array for seamless loop
                {
                  domain: "HR",
                  title: "AI Recruitment Assistant",
                  description: "Screens candidates, updates tracker, sends feedback, and evaluates resumes using AI prompts.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6h3v7c0 .55.45 1 1 1h9c.55 0 1-.45 1-1v-7h3v6c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2zM18 7h-2.5c-.71 0-1.36.37-1.73.99L12 10.5 10.23 7.99c-.37-.62-1.02-.99-1.73-.99H6c-1.1 0-2 .9-2 2v3h18V9c0-1.1-.9-2-2-2z"/>
                    </svg>
                  ),
                  features: ["Resume scoring", "Candidate evaluation", "Automated feedback"]
                },
                {
                  domain: "Sales", 
                  title: "Lead Qualification Agent",
                  description: "Qualifies leads using vector search, sends follow-ups, and books demos automatically.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"/>
                    </svg>
                  ),
                  features: ["Lead scoring", "Auto follow-up", "Demo booking"]
                },
                {
                  domain: "Marketing",
                  title: "Content Generation Bot",
                  description: "Creates multi-channel content, optimizes campaigns, and analyzes sentiment.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z"/>
                    </svg>
                  ),
                  features: ["Content creation", "Campaign optimization", "Sentiment analysis"]
                },
                {
                  domain: "Finance",
                  title: "Document Processor",
                  description: "Summarizes financial documents, classifies invoices, and parses expenses from Gmail.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                    </svg>
                  ),
                  features: ["Document analysis", "Invoice routing", "Expense tracking"]
                },
                {
                  domain: "Operations",
                  title: "Task Management System",
                  description: "Routes tickets, analyzes workspace data, and summarizes meeting notes automatically.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  ),
                  features: ["Ticket routing", "Workspace analysis", "Meeting summaries"]
                },
                {
                  domain: "Education",
                  title: "AI Learning Assistant",
                  description: "Provides revision support, creates voice quizzes, and generates content from PDFs.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
                    </svg>
                  ),
                  features: ["AI tutoring", "Quiz generation", "Voice interaction"]
                },
                {
                  domain: "Healthcare",
                  title: "Medical Records Bot",
                  description: "Processes patient data, schedules appointments, and sends health reminders automatically.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 8h-2v3h-3v2h3v3h2v-3h3v-2h-3V8zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z"/>
                    </svg>
                  ),
                  features: ["Patient data processing", "Appointment scheduling", "Health reminders"]
                },
                {
                  domain: "E-commerce",
                  title: "Inventory Manager",
                  description: "Tracks stock levels, predicts demand, and automates reordering workflows.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7 4V2C7 1.45 7.45 1 8 1h8c.55 0 1 .45 1 1v2h4c.55 0 1 .45 1 1s-.45 1-1 1h-1v11c0 1.1-.9 2-2 2H6c-1.1 0-2-.9-2-2V6H3c-.55 0-1-.45-1-1s.45-1 1-1h4zM9 3v1h6V3H9zm2 12c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55.45 1 1 1zm2-6v5c0 .55.45 1 1 1s1-.45 1-1V9c0-.55-.45-1-1-1s-1 .45-1 1z"/>
                    </svg>
                  ),
                  features: ["Stock tracking", "Demand prediction", "Auto reordering"]
                },
                {
                  domain: "Legal",
                  title: "Contract Analyzer",
                  description: "Reviews legal documents, extracts key terms, and flags potential issues.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/>
                    </svg>
                  ),
                  features: ["Document review", "Term extraction", "Risk assessment"]
                },
                {
                  domain: "Real Estate",
                  title: "Property Evaluator",
                  description: "Analyzes market trends, estimates property values, and generates reports.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                    </svg>
                  ),
                  features: ["Market analysis", "Property valuation", "Report generation"]
                },
                {
                  domain: "Customer Service",
                  title: "Support Ticket Router",
                  description: "Categorizes tickets, assigns priority levels, and routes to appropriate teams.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"/>
                    </svg>
                  ),
                  features: ["Ticket categorization", "Priority assignment", "Team routing"]
                },
                {
                  domain: "Manufacturing",
                  title: "Quality Control Agent",
                  description: "Monitors production data, detects anomalies, and triggers quality alerts.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ),
                  features: ["Production monitoring", "Anomaly detection", "Quality alerts"]
                },
                {
                  domain: "Travel",
                  title: "Trip Planning Assistant",
                  description: "Books flights, finds accommodations, and creates personalized itineraries.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                    </svg>
                  ),
                  features: ["Flight booking", "Hotel search", "Itinerary creation"]
                },
                {
                  domain: "Food & Beverage",
                  title: "Menu Optimizer",
                  description: "Analyzes sales data, suggests menu changes, and manages supplier orders.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                    </svg>
                  ),
                  features: ["Sales analysis", "Menu optimization", "Supplier management"]
                },
                {
                  domain: "Logistics",
                  title: "Route Optimization Bot",
                  description: "Plans delivery routes, tracks shipments, and optimizes delivery schedules.",
                  iconSvg: (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                    </svg>
                  ),
                  features: ["Route planning", "Shipment tracking", "Schedule optimization"]
                }
              ]).map((project, idx) => (
                <div key={idx} className="flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                      {project.iconSvg}
                    </div>
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-bold">
                      {project.domain}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                    {project.description}
                  </p>
                  <div className="space-y-2">
                    {project.features.map((feature, featureIdx) => (
                      <div key={featureIdx} className="flex items-center text-sm text-gray-500">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Master the <span className="text-purple-600">No-Code AI Stack</span><br />
              That Powers <span className="text-purple-600">Fortune 500</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              <span className="text-purple-600 font-semibold">50+ powerful tools</span> for building agentic AI systems. 
              <span className="text-gray-800 font-medium"> No coding required - just builder thinking!</span>
            </p>
          </div>

          {/* Scrolling Tools Carousel */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll-left space-x-6" style={{
              width: 'calc(200px * 80)', // 80 tools * 200px width
              animation: 'scroll-left 80s linear infinite'
            }}>
              {[
                { name: "OpenAI", description: "GPT & DALL-E", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" },
                { name: "Claude", description: "Anthropic AI", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/anthropic.svg" },
                { name: "n8n", description: "Workflow Automation", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/n8n.svg" },
                { name: "Make", description: "Visual Automation", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/make.svg" },
                { name: "Zapier", description: "App Integration", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/zapier.svg" },
                { name: "Supabase", description: "Vector Database", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/supabase.svg" },
                { name: "Pinecone", description: "Vector Search", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/pinecone.svg" },
                { name: "Weaviate", description: "Vector Database", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/weaviate.svg" },
                { name: "LangChain", description: "AI Framework", logo: "https://avatars.githubusercontent.com/u/126733545?s=200&v=4" },
                { name: "AutoGen", description: "Multi-Agent Chat", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" },
                { name: "Crew AI", description: "Agent Framework", logo: "https://avatars.githubusercontent.com/u/120895197?s=280&v=4" },
                { name: "Flowise", description: "Visual LLM Apps", logo: "https://docs.flowiseai.com/img/logo.svg" },
                { name: "LangFlow", description: "Visual AI Chains", logo: "https://github.com/langflow-ai/langflow/raw/main/docs/static/img/langflow-logo.png" },
                { name: "Bubble", description: "No-Code Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/bubble.svg" },
                { name: "Airtable", description: "Database Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/airtable.svg" },
                { name: "Notion", description: "Knowledge Base", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/notion.svg" },
                { name: "Telegram", description: "Bot Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/telegram.svg" },
                { name: "Discord", description: "Bot Integration", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/discord.svg" },
                { name: "Slack", description: "Workspace Bot", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" },
                { name: "WhatsApp", description: "Business API", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/whatsapp.svg" },
                { name: "Gmail", description: "Email Automation", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/gmail.svg" },
                { name: "Google Sheets", description: "Data Management", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google-sheets.svg" },
                { name: "Calendly", description: "Booking System", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/calendly.svg" },
                { name: "Typeform", description: "Form Builder", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/typeform.svg" },
                { name: "Stripe", description: "Payment Processing", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/stripe.svg" },
                { name: "Shopify", description: "E-commerce Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/shopify.svg" },
                { name: "HubSpot", description: "CRM Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/hubspot.svg" },
                { name: "Salesforce", description: "CRM Integration", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/salesforce.svg" },
                { name: "Twilio", description: "Communication API", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/twilio.svg" },
                { name: "AWS", description: "Cloud Services", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/aws.svg" },
                { name: "Google Cloud", description: "AI/ML Services", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google-cloud.svg" },
                { name: "Azure", description: "Cloud Services", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/azure.svg" },
                { name: "Vercel", description: "Deployment Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/vercel.svg" },
                { name: "Netlify", description: "Web Hosting", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/netlify.svg" },
                { name: "GitHub", description: "Code Repository", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/github.svg" },
                { name: "Retool", description: "Internal Tools", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/retool.svg" },
                { name: "Webflow", description: "Visual Web Design", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/webflow.svg" },
                { name: "Figma", description: "Design Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/figma.svg" },
                { name: "Framer", description: "Interactive Design", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/framer.svg" },
                { name: "Loom", description: "Video Messages", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/loom.svg" },
                { name: "Gemini", description: "Google AI", logo: "https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg" }
              ].concat([
                // Duplicate the array for seamless loop
                { name: "OpenAI", description: "GPT & DALL-E", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" },
                { name: "Claude", description: "Anthropic AI", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/anthropic.svg" },
                { name: "n8n", description: "Workflow Automation", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/n8n.svg" },
                { name: "Make", description: "Visual Automation", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/make.svg" },
                { name: "Zapier", description: "App Integration", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/zapier.svg" },
                { name: "Supabase", description: "Vector Database", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/supabase.svg" },
                { name: "Pinecone", description: "Vector Search", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/pinecone.svg" },
                { name: "Weaviate", description: "Vector Database", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/weaviate.svg" },
                { name: "LangChain", description: "AI Framework", logo: "https://avatars.githubusercontent.com/u/126733545?s=200&v=4" },
                { name: "AutoGen", description: "Multi-Agent Chat", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" },
                { name: "Crew AI", description: "Agent Framework", logo: "https://avatars.githubusercontent.com/u/120895197?s=280&v=4" },
                { name: "Flowise", description: "Visual LLM Apps", logo: "https://docs.flowiseai.com/img/logo.svg" },
                { name: "LangFlow", description: "Visual AI Chains", logo: "https://github.com/langflow-ai/langflow/raw/main/docs/static/img/langflow-logo.png" },
                { name: "Bubble", description: "No-Code Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/bubble.svg" },
                { name: "Airtable", description: "Database Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/airtable.svg" },
                { name: "Notion", description: "Knowledge Base", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/notion.svg" },
                { name: "Telegram", description: "Bot Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/telegram.svg" },
                { name: "Discord", description: "Bot Integration", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/discord.svg" },
                { name: "Slack", description: "Workspace Bot", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" },
                { name: "WhatsApp", description: "Business API", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/whatsapp.svg" },
                { name: "Gmail", description: "Email Automation", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/gmail.svg" },
                { name: "Google Sheets", description: "Data Management", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google-sheets.svg" },
                { name: "Calendly", description: "Booking System", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/calendly.svg" },
                { name: "Typeform", description: "Form Builder", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/typeform.svg" },
                { name: "Stripe", description: "Payment Processing", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/stripe.svg" },
                { name: "Shopify", description: "E-commerce Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/shopify.svg" },
                { name: "HubSpot", description: "CRM Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/hubspot.svg" },
                { name: "Salesforce", description: "CRM Integration", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/salesforce.svg" },
                { name: "Twilio", description: "Communication API", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/twilio.svg" },
                { name: "AWS", description: "Cloud Services", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/aws.svg" },
                { name: "Google Cloud", description: "AI/ML Services", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google-cloud.svg" },
                { name: "Azure", description: "Cloud Services", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/azure.svg" },
                { name: "Vercel", description: "Deployment Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/vercel.svg" },
                { name: "Netlify", description: "Web Hosting", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/netlify.svg" },
                { name: "GitHub", description: "Code Repository", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/github.svg" },
                { name: "Retool", description: "Internal Tools", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/retool.svg" },
                { name: "Webflow", description: "Visual Web Design", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/webflow.svg" },
                { name: "Figma", description: "Design Platform", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/figma.svg" },
                { name: "Framer", description: "Interactive Design", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/framer.svg" },
                { name: "Loom", description: "Video Messages", logo: "https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/loom.svg" },
                { name: "Gemini", description: "Google AI", logo: "https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg" }
              ]).map((tool, idx) => (
                <div key={idx} className="flex-shrink-0 w-44 bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100 text-center group">
                  <div className="h-12 mb-3 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <img 
                      src={tool.logo} 
                      alt={tool.name}
                      className="max-h-8 max-w-8 object-contain"
                      onError={(e) => {
                        // Fallback to text if image fails to load
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                    <div className="text-2xl font-bold text-purple-600 hidden">
                      {tool.name.charAt(0)}
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-purple-600 transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-gray-500 text-xs">
                    {tool.description}
                  </p>
                                 </div>
               ))}
              </div>
          </div>
        </div>
      </section>

      {/* Section Separator */}
      <div className="border-t border-gray-200"></div>

      {/* FAQ Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Frequently Asked <span className="text-purple-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Everything you need to know about becoming an AI Founder
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-0">
              {[
                {
                  question: "Do I need coding experience to succeed in this program?",
                  answer: "No! This is a no-code program designed for doers, not just coders. You need curiosity, commitment, and the willingness to think in systems. We'll teach you everything else."
                },
                {
                  question: "How is this different from other AI courses?",
                  answer: "Most courses teach theory. We build systems. You'll create 100+ real AI automations, launch actual products, and learn to sell AI services. This is transformation, not just education."
                },
                {
                  question: "What professional paths can this program support?",
                  answer: "The program builds skills used in AI automation consulting, workflow design, AI product work, automation operations, and AI service building."
                },
                {
                  question: "How long do I have access to the course materials?",
                  answer: "1 year of full access to all course materials, plus lifetime access to our AI builder community. Live events happen on weekends and are not recorded."
                },
                {
                  question: "Can I build these automations for my current company?",
                  answer: "Absolutely. Many learners apply these automations inside their current teams to improve workflows and demonstrate AI leadership."
                },
                {
                  question: "What if I get stuck or need help?",
                  answer: "You get 1-1 live support sessions with expert mentors, access to live doubt sessions, and our active community of AI builders ready to help you succeed."
                }
              ].map((faq, index) => (
                <div key={index} className="border-b border-gray-300 last:border-b-0">
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer text-left text-gray-900 hover:text-gray-700 text-lg font-medium py-6 hover:no-underline list-none">
                      <span>{faq.question}</span>
                      <svg 
                        className="w-5 h-5 text-gray-500 transform transition-transform group-open:rotate-180" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="text-gray-600 pb-6 pt-2 leading-relaxed text-base">
                      {faq.answer}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-4 overflow-hidden cursor-tracker-section" style={{ backgroundColor: '#1D4ED8' }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* AI Agent Network Visualization */}
          <div className="absolute top-16 left-16">
            <div className="relative">
              {/* Central AI Agent */}
              <div className="w-12 h-12 bg-white/20 rounded-lg animate-pulse flex items-center justify-center backdrop-blur-sm">
                <div className="text-white text-lg">🤖</div>
              </div>
              {/* Connected Sub-Agents */}
              <div className="absolute top-2 left-16 w-8 h-8 bg-blue-300/30 rounded-lg animate-pulse delay-100 flex items-center justify-center backdrop-blur-sm">
                <div className="text-white text-sm">⚡</div>
              </div>
              <div className="absolute -top-2 left-8 w-6 h-6 bg-cyan-300/30 rounded-lg animate-pulse delay-200 flex items-center justify-center backdrop-blur-sm">
                <div className="text-white text-xs">🔍</div>
              </div>
              <div className="absolute top-12 left-4 w-7 h-7 bg-green-300/30 rounded-lg animate-pulse delay-300 flex items-center justify-center backdrop-blur-sm">
                <div className="text-white text-sm">✓</div>
              </div>
              {/* Connection Lines */}
              <svg className="absolute inset-0 w-24 h-20 pointer-events-none">
                <line x1="12" y1="12" x2="20" y2="10" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse" />
                <line x1="12" y1="12" x2="14" y2="6" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse delay-100" />
                <line x1="12" y1="12" x2="10" y2="18" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2,2" className="animate-pulse delay-200" />
              </svg>
            </div>
          </div>

          {/* Automation Workflow Blocks */}
          <div className="absolute bottom-20 right-20">
            <div className="space-y-3">
              {/* Workflow Chain */}
              <div className="flex items-center space-x-2">
                <div className="w-10 h-6 bg-white/20 rounded flex items-center justify-center animate-pulse backdrop-blur-sm">
                  <div className="text-xs text-white font-bold">IF</div>
                </div>
                <div className="w-1 h-0.5 bg-white/40"></div>
                <div className="w-12 h-6 bg-white/25 rounded flex items-center justify-center animate-pulse delay-100 backdrop-blur-sm">
                  <div className="text-xs text-white font-bold">AI</div>
                </div>
                <div className="w-1 h-0.5 bg-white/40"></div>
                <div className="w-10 h-6 bg-white/20 rounded flex items-center justify-center animate-pulse delay-200 backdrop-blur-sm">
                  <div className="text-xs text-white font-bold">DO</div>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                <div className="w-8 h-6 bg-cyan-400/30 rounded flex items-center justify-center animate-pulse delay-150 backdrop-blur-sm">
                  <div className="w-3 h-3 bg-white rounded-full"></div>
                </div>
                <div className="w-1 h-0.5 bg-white/40"></div>
                <div className="w-10 h-6 bg-green-400/30 rounded flex items-center justify-center animate-pulse delay-250 backdrop-blur-sm">
                  <div className="text-xs text-white font-bold">✓</div>
                </div>
              </div>
            </div>
          </div>

          {/* Vector Database Visualization */}
          <div className="absolute top-1/3 right-16">
            <div className="relative">
              <div className="w-16 h-10 bg-white/15 rounded-lg animate-pulse backdrop-blur-sm">
                <div className="absolute inset-2 space-y-1">
                  <div className="h-1 bg-white/60 rounded w-full"></div>
                  <div className="h-1 bg-white/60 rounded w-3/4"></div>
                  <div className="h-1 bg-white/60 rounded w-1/2"></div>
                </div>
              </div>
              <div className="absolute -bottom-4 left-0 text-xs text-white/80 font-mono">VECTOR DB</div>
            </div>
          </div>

          {/* AI Processing Indicators */}
          <div className="absolute top-20 right-1/3">
            <div className="space-y-2">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping delay-100"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping delay-200"></div>
                <div className="text-xs text-blue-200 font-mono">AI THINKING</div>
              </div>
            </div>
          </div>

          {/* Automation Gears */}
          <div className="absolute bottom-1/3 left-1/4">
            <div className="relative">
              <svg width="40" height="40" className="text-white/30 animate-spin" fill="currentColor" viewBox="0 0 24 24" style={{ animationDuration: '8s' }}>
                <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5M19.43 12.98C19.47 12.66 19.5 12.34 19.5 12C19.5 11.66 19.47 11.34 19.43 11.02L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.97 19.05 5.05L16.56 6.05C16.04 5.65 15.48 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.52 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.21 8.95 2.27 9.22 2.46 9.37L4.57 11.02C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.66 4.57 12.98L2.46 14.63C2.27 14.78 2.21 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.95C7.96 18.35 8.52 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.48 18.68 16.04 18.34 16.56 17.95L19.05 18.95C19.27 19.04 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.98Z"/>
              </svg>
              <div className="absolute -bottom-4 left-0 text-xs text-white/80 font-mono">AUTO</div>
            </div>
          </div>

          {/* Cursor Tracker Element */}
          <div id="cursor-tracker" className="absolute w-4 h-4 bg-white/40 rounded-full pointer-events-none transition-all duration-300 ease-out backdrop-blur-sm"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight whitespace-nowrap">
            Ready to Become an <span className="text-blue-200">AI Founder</span>?
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-4xl mx-auto">
            Join the next generation of AI builders. Transform from outsider to founder in just 4 months.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://academy.10xdataanalyst.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              Start Building Now
            </a>
            <button 
              onClick={() => openPopup()}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
            >
              Book Strategy Call
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactPopup
        isOpen={popupState.isOpen}
        onClose={closePopup}
        title={popupState.title}
        subtitle={popupState.subtitle}
        formType={popupState.formType}
        interestedProgram={popupState.interestedProgram}
      />
    </div>
  );
};

export default AgenticAICourse; 
